import { Badge } from "@workspace/web-ui/components/badge";
import { Button } from "@workspace/web-ui/components/button";
import { Separator } from "@workspace/web-ui/components/separator";
import React from "react";

import { PendingMedication } from "~/features/dashboard/pages/medicals/types";
import { MedicationPaymentModal } from "~/features/dashboard/components/medication-payment";

import { formatMedicationDate } from "@workspace/lib/date";
import { useParams } from "@tanstack/react-router";

interface Medication {
  medicalItems: PendingMedication[];
  program?: string;
  isLoading?: boolean;
  totalCount?: number;
}

export const getStatusConfig = (item: PendingMedication) => {
  let status: string

  status = item.payment_status

  const statusMap = {
    "not_paid": { color: "bg-[#FEEBEB] text-[#F53535] hover:bg-[#FEEBEB]", text: "Priority", status: "not_paid" },
  };

  return statusMap[status as keyof typeof statusMap];
};

export function PendingMedicationsPayments({ medicalItems, totalCount }: Medication) {
  const [payForMed, setPayForMed] = React.useState(false);
  const [reqMedPrescriptionId, setReqMedPrescriptionId] = React.useState("");
  const { program } = useParams({ strict: false })

  const renderButtons = (item: PendingMedication) => {
    const statusConfig = getStatusConfig(item)

    return (
      <div key={item.medication.medication_id} className="flex flex-col border border-[#0404041A] shadow-sm rounded-[8px] mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full py-3 sm:py-[14px] px-3 sm:px-[20px] gap-3 sm:gap-0">
          <div className="flex flex-col gap-1 w-full">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-medium text-base sm:text-lg text-[#2D2F35]">{item.medication.name}</h3>
              <span className="text-xs sm:text-sm">({item.medication.duration} supply)</span>
              <Badge className={`${statusConfig.color} text-xs font-medium`}><span className=" text-[00C781] mr-1 ">{"\u2B24"}</span> {statusConfig.text}</Badge>
            </div>

            <div className="flex">
              <h6 className="text-xs sm:text-sm font-normal text-[#202020CC] text-left">
                Prescribed: {formatMedicationDate(item.prescribed_on)} <span className="mx-1 text-[#C9C9CC]">{"\u2022"}</span>
                {item.medication.dosage} <span className="mx-1 text-[#C9C9CC]">{"\u2022"}</span> {item.medication.duration}
              </h6>
            </div>
          </div>

          <Button
            variant="default"
            onClick={() => {
              setReqMedPrescriptionId(item.prescription_id || "")
              setPayForMed(true)
            }}
            className="sm:ml-4 rounded-lg font-semibold w-full sm:w-auto text-sm mt-2 sm:mt-0"
          >
            ${item.medication.cost} - Pay now
          </Button>
        </div>
        {/* Instructions */}
        {item.medication?.instructions && (
          <div className="flex flex-col gap-1 w-full bg-[#F0F5FF] py-3 sm:py-[12px] px-3 sm:px-[20px] transition-colors border-[#4272DD] border-l-4 rounded-b-[8px]">
            <h6 className="text-xs sm:text-sm font-medium text-[#4272DD] text-left">
              Instructions
            </h6>
            <p className="text-xs sm:text-sm font-normal text-[#202020CC] text-left">
              {item.medication?.instructions}
            </p>
          </div>
        )}

      </div>
    )
  }


  return (
    <div className="border rounded-xl shadow-sm bg-white w-full text-center mb-4 ">
      <h3 className="text-base sm:text-lg items-center flex font-medium text-left p-4 sm:p-5 text-[#1A202C]">Pending payments <Badge className="ml-2 size-4 rounded-full bg-[#4272DD] border border-[#0404041A] p-2 justify-center items-center text-center text-white text-xs">
        {totalCount || 0}
      </Badge></h3>
      <Separator />
      <div className="space-y-4 bg-card rounded-[8px] p-3 sm:p-4">
        {medicalItems.map((med) => renderButtons(med))}
      </div>

      {payForMed && reqMedPrescriptionId && <MedicationPaymentModal
        program={program === 'weight-loss' ? 'weight_loss' : 'urgent_care'}
        prescription_id={reqMedPrescriptionId}
        open={payForMed}
        onClose={() => setPayForMed(false)}
      />}
    </div>
  )
}
